import type { AccountKind } from '@theailab-dev/parchment';
import axios, { AxiosError } from 'axios';
import apiClient from '../lib/apiClient';
import { createBasicToken, getAccessToken, getRefreshToken, setRefreshToken, getCookie } from '../utils/auth';

// Basic 토큰 방식 로그인 요청 (body에 사용되지 않음, 헤더에만 사용)
export interface LoginRequest {
  email: string;
  password: string;
}

// 로그인 응답
export interface LoginResponse {
  success: boolean;
  accessToken?: string;
  refreshToken?: string;
}

// 토큰 갱신 응답
export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken?: string;
}

// 관리자 사용자 정보 (백엔드 /auth/me 응답)
export interface User {
  id: string;
  email: string;
  name: string;
  kind: AccountKind; // 관리자 구분 (ADMIN, STAFF 등)
  username?: string; // 하위 호환성을 위해 유지
  [key: string]: unknown; // 백엔드 Staff와 호환성을 위한 인덱스 시그니처
}

// 로그인 (Basic 토큰 사용, 쿠키로 accessToken 받음)
export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  // 로그인 요청만 Basic 토큰 사용 (apiClient 인터셉터를 우회하기 위해 직접 axios 사용)
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4010';
  const loginUrl = `${API_URL}/api/auth/login`; // 백엔드가 /api prefix 사용

  try {
    const basicToken = createBasicToken(data.email, data.password);

    console.log('🔐 로그인 요청 시작:', {
      url: loginUrl,
      email: data.email,
      hasPassword: !!data.password,
      timestamp: new Date().toISOString(),
    });

    // axios 인스턴스 생성 (로그인 요청 전용, 쿠키 포함)
    const loginClient = axios.create({
      baseURL: API_URL,
      timeout: 10000, // 10초로 단축
      withCredentials: true, // 쿠키를 받기 위해 필수
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // 요청 인터셉터로 로깅
    loginClient.interceptors.request.use(
      (config) => {
        console.log('📤 POST 요청 전송:', {
          url: config.url,
          fullUrl: `${config.baseURL}${config.url}`,
          method: config.method,
          headers: config.headers,
          withCredentials: config.withCredentials,
          timestamp: new Date().toISOString(),
        });
        return config;
      },
      (error) => {
        console.error('❌ 요청 설정 오류:', error);
        return Promise.reject(error);
      },
    );

    // 응답 인터셉터로 로깅 및 쿠키 확인
    loginClient.interceptors.response.use(
      (response) => {
        console.log('📥 응답 수신:', {
          status: response.status,
          statusText: response.statusText,
          data: response.data,
          headers: response.headers,
          cookies: document.cookie,
          timestamp: new Date().toISOString(),
        });
        return response;
      },
      (error) => {
        console.error('❌ 응답 오류:', {
          message: error.message,
          code: error.code,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers,
          request: error.request ? '요청 전송됨' : '요청 전송 안 됨',
          timestamp: new Date().toISOString(),
        });
        return Promise.reject(error);
      },
    );

    console.log('🚀 로그인 요청 시작:', {
      url: `${API_URL}/api/auth/login`,
      method: 'POST',
      withCredentials: true,
      timestamp: new Date().toISOString(),
    });

    console.log('🚀 실제 POST 요청 전송 직전:', {
      url: `${API_URL}/api/auth/login`,
      timestamp: new Date().toISOString(),
    });

    const response = await loginClient.post<LoginResponse>(
      '/api/auth/login',
      {}, // body는 비우거나 필요시 추가
      {
        headers: {
          Authorization: `Basic ${basicToken}`,
        },
        validateStatus: (status) => {
          // 200-299 범위의 모든 상태 코드를 성공으로 처리
          return status >= 200 && status < 300;
        },
      },
    );

    console.log('✅ 로그인 성공 - 응답 받음:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      hasRefreshTokenInBody: !!response.data?.refreshToken,
      hasAccessTokenInBody: !!response.data?.accessToken,
      headers: response.headers,
      cookies: document.cookie,
      allCookies: document.cookie.split(';').map((c) => c.trim()),
      timestamp: new Date().toISOString(),
    });

    // 응답 body에 refreshToken이 포함된 경우 sessionStorage에 저장
    if (response.data?.refreshToken) {
      setRefreshToken(response.data.refreshToken);
      console.log('💾 refreshToken을 sessionStorage에 저장:', {
        tokenLength: response.data.refreshToken.length,
        tokenPreview: response.data.refreshToken.substring(0, 20) + '...',
      });
    } else {
      console.warn('⚠️ 로그인 응답에 refreshToken이 포함되지 않았습니다.');
    }

    // 쿠키에서 accessToken과 refreshToken 확인
    // 쿠키가 설정되는 데 약간의 시간이 필요할 수 있으므로 짧은 지연 후 확인
    await new Promise((resolve) => setTimeout(resolve, 100));

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();

    console.log('🍪 로그인 후 토큰 확인:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      fromCookie: !!getCookie('refreshToken'),
      fromSessionStorage: !!sessionStorage.getItem('refreshToken'),
      allCookies: document.cookie.split(';').map((c) => c.trim()),
    });

    if (!accessToken) {
      console.warn('⚠️ accessToken을 찾을 수 없습니다.');
    }
    if (!refreshToken) {
      console.warn('⚠️ refreshToken을 찾을 수 없습니다.');
    }

    // 200, 201, 204 등 모든 성공 상태 코드 처리
    if (response.status >= 200 && response.status < 300) {
      // 응답 데이터가 있으면 사용, 없으면 기본값
      return response.data || { success: true };
    }

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;

    // 타임아웃 에러인 경우
    if (axiosError.code === 'ECONNABORTED' || axiosError.message.includes('timeout')) {
      console.error('⏱️ 요청 타임아웃:', {
        message: '서버 응답이 10초 내에 오지 않았습니다.',
        url: `${API_URL}/api/auth/login`,
        timestamp: new Date().toISOString(),
      });
      throw new Error('서버 응답 시간이 초과되었습니다. 네트워크 연결과 서버 상태를 확인해주세요.');
    }

    // 네트워크 에러인 경우
    if (!axiosError.response && axiosError.request) {
      console.error('🌐 네트워크 에러:', {
        message: '서버에 연결할 수 없습니다.',
        url: `${API_URL}/api/auth/login`,
        code: axiosError.code,
        timestamp: new Date().toISOString(),
      });
      throw new Error('서버에 연결할 수 없습니다. 서버가 실행 중인지 확인해주세요.');
    }

    // CORS 에러인 경우
    if (axiosError.message.includes('CORS') || axiosError.code === 'ERR_NETWORK') {
      console.error('🚫 CORS 에러:', {
        message: 'CORS 정책으로 인해 요청이 차단되었습니다.',
        url: `${API_URL}/api/auth/login`,
        timestamp: new Date().toISOString(),
      });
      throw new Error('CORS 정책 오류가 발생했습니다. 백엔드 CORS 설정을 확인해주세요.');
    }

    console.error('❌ 로그인 실패 상세:', {
      message: axiosError.message,
      code: axiosError.code,
      status: axiosError.response?.status,
      statusText: axiosError.response?.statusText,
      data: axiosError.response?.data,
      url: axiosError.config?.url,
      request: axiosError.request ? '요청 전송됨' : '요청 전송 안 됨',
      timestamp: new Date().toISOString(),
    });
    throw error;
  }
};

// 로그아웃 (백엔드에서 쿠키 삭제)
export const logout = async (): Promise<void> => {
  await apiClient.post('/auth/logout');
};

// 토큰 갱신 (refreshToken은 쿠키로 자동 전송됨)
export const refreshToken = async (): Promise<RefreshTokenResponse> => {
  // refreshToken은 쿠키에서 자동으로 전송되므로 body는 비움
  const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', {});
  return response.data;
};

// 현재 사용자 정보 조회
export const getCurrentUser = async (): Promise<User> => {
  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4010';
  const fullUrl = `${API_URL}/api/auth/me`; // 백엔드가 /api prefix 사용

  console.log('🔍 사용자 정보 조회 요청:', {
    url: fullUrl,
    endpoint: '/auth/me',
    timestamp: new Date().toISOString(),
  });

  try {
    const response = await apiClient.get<User>('/auth/me');
    console.log('✅ 사용자 정보 조회 성공:', {
      data: response.data,
      dataType: typeof response.data,
      hasId: 'id' in (response.data || {}),
      hasEmail: 'email' in (response.data || {}),
      hasName: 'name' in (response.data || {}),
      hasKind: 'kind' in (response.data || {}),
      keys: response.data ? Object.keys(response.data) : [],
    });

    // 타입 검증: 필수 필드 확인
    if (!response.data) {
      throw new Error('사용자 정보가 없습니다.');
    }

    const user = response.data;
    if (!user.id || !user.email || !user.name || !user.kind) {
      console.warn('⚠️ 사용자 정보 필수 필드 누락:', {
        id: user.id,
        email: user.email,
        name: user.name,
        kind: user.kind,
        receivedData: user,
      });
    }

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error('❌ 사용자 정보 조회 실패:', {
      errorType: error?.constructor?.name,
      errorMessage: error instanceof Error ? error.message : String(error),
      url: fullUrl,
      status: axiosError?.response?.status,
      statusText: axiosError?.response?.statusText,
      responseData: axiosError?.response?.data,
      requestUrl: axiosError?.config?.url,
      requestMethod: axiosError?.config?.method,
      isNetworkError: !axiosError?.response && axiosError?.request,
    });
    throw error;
  }
};
